
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui';
import { truncateBody } from '@/lib/format';
import { cn } from '@/lib/utils';

interface SubmissionSuccessProps {
  type: 'prayer_request' | 'praise_report' | 'prayer' | 'praise';
  body?: string;
  onSubmitAnother: () => void;
  onPrayForOthers?: () => void;
}

export function SubmissionSuccess({
  type,
  body,
  onSubmitAnother,
  onPrayForOthers,
}: SubmissionSuccessProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation after mount
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const isPraise = type === 'praise_report' || type === 'praise';
  const typeLabel = isPraise ? 'Answered Prayer' : 'Prayer Request';
  const bodyPreview = body ? truncateBody(body, 120) : '';

  return (
    <div
      className={cn(
        'flex flex-col items-center text-center py-8 px-4 transition-all duration-500',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      )}
    >
      {/* Animated Checkmark */}
      <div
        className={cn(
          'w-20 h-20 rounded-full flex items-center justify-center mb-6',
          'bg-green-100 dark:bg-green-900/30',
          'transition-transform duration-500',
          isVisible ? 'scale-100' : 'scale-0'
        )}
      >
        <svg
          className="w-10 h-10 text-green-600 dark:text-green-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
            className={cn(
              'transition-all duration-500 delay-200',
              isVisible ? 'stroke-dashoffset-0' : ''
            )}
            style={{
              strokeDasharray: 24,
              strokeDashoffset: isVisible ? 0 : 24,
              transition: 'stroke-dashoffset 0.5s ease-out 0.2s',
            }}
          />
        </svg>
      </div>

      {/* Success Message */}
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
        {typeLabel} Submitted
      </h2>

      <p className="text-slate-600 dark:text-slate-400 mb-4 max-w-sm">
        {isPraise
          ? 'Thank you for sharing your praise! Your testimony encourages others.'
          : 'Your prayer has been received. Our community will be lifting you up in prayer.'}
      </p>

      {bodyPreview && (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic mb-8 max-w-sm line-clamp-3">
          &ldquo;{bodyPreview}&rdquo;
        </p>
      )}

      {!bodyPreview && <div className="mb-8" />}

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button variant="primary" fullWidth onClick={onSubmitAnother}>
          Submit Another
        </Button>
        {onPrayForOthers && (
          <Button variant="outline" fullWidth onClick={onPrayForOthers}>
            Pray for Others
          </Button>
        )}
      </div>

      {/* Encouraging Message */}
      <p className="mt-8 text-sm text-slate-500 dark:text-slate-400 italic">
        &ldquo;Therefore I tell you, whatever you ask in prayer, believe that you have
        received it, and it will be yours.&rdquo;
        <span className="block mt-1 not-italic font-medium">— Mark 11:24</span>
      </p>
    </div>
  );
}
